import { useState, useEffect, useCallback } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import Notice from '../../components/common/Notice';
import { apiService } from '../../services/api.service';
import { API_ENDPOINTS } from '../../config/environment';
import { formatTL } from '../../utils/money';
import './FleetModelBreakdown.css';

/**
 * Haftanın paketleri — restoranla yapılan anlaşmanın İŞ MODELİNE göre.
 *
 * Model, Hızlıyo'nun restoran sözleşmesinde seçtiği üç yapıdan biridir (CRM'deki "Kurye Firması
 * İş Modeli"). Filo için fark paranın yönüdür; bu yüzden her kart önce yönü söyler:
 *
 *   1  Restoran → Hızlıyo → Siz      Hızlıyo'dan alacaklısınız, faturayı SİZ kesersiniz.
 *   2  Restoran → Siz → Hızlıyo      Paket bedelini restorandan alırsınız, Hızlıyo size
 *                                     paket başı yazılım bedeli faturası keser.
 *   3  Restoran → Siz                Hızlıyo ile aranızda fatura yoktur.
 *
 * Adet ve tutarlar sunucudan gelir (`fleet-payments/models`); paket kümesi Hızlıyo'nun
 * faturada saydığı kümeyle aynı kuraldan çıkar, ekranda yeniden hesap yapılmaz.
 */
const MODELS = {
    1: {
        title: 'Hızlıyo tahsil eder',
        path: ['Restoran', 'Hızlıyo', 'Siz'],
        amountLabel: 'Hızlıyo\'dan alacağınız',
        tone: 'receivable',
        note: 'Hafta kapanınca hakediş kaydı açılır; faturasını siz kesersiniz.',
        empty: 'Bu hafta Hızlıyo\'nun tahsil ettiği paketiniz yok.',
    },
    2: {
        title: 'Firmaya entegrasyon bedeli',
        path: ['Restoran', 'Siz', 'Hızlıyo'],
        amountLabel: 'Hızlıyo\'ya ödeyeceğiniz (tahmini)',
        tone: 'payable',
        note: 'Paket bedelini restorandan siz alırsınız. Hızlıyo size yazılım bedeli faturası keser; kesilen fatura Faturalar sekmesinde görünür ve geçerli tutar odur.',
        empty: 'Bu hafta entegrasyon bedelli paketiniz yok.',
    },
    3: {
        title: 'Restoran kontör tüketir',
        path: ['Restoran', 'Siz'],
        amountLabel: 'Hızlıyo ile fatura',
        tone: 'none',
        note: 'Paket bedelini restorandan siz alırsınız. Hızlıyo payını restoranın kontöründen alır.',
        empty: 'Bu hafta bu modelde paketiniz yok.',
    },
};

const amountOf = (group) => {
    if (group.model === 1) return formatTL(group.receivableAmount);
    if (group.model === 2) return `${formatTL(group.payableAmount)} + KDV`;
    return 'Yok';
};

const FleetModelBreakdown = ({ year, week }) => {
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [openModel, setOpenModel] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await apiService.get(API_ENDPOINTS.FLEET_PANEL.FLEET_PAYMENT_MODELS(year, week));
            const data = res.data?.models ? res.data : res.data?.data;
            if (res.success && data?.models) setGroups(data.models);
            else { setGroups([]); setError(res.error || 'Model dökümü yüklenemedi.'); }
        } catch {
            setGroups([]);
            setError('Model dökümü yüklenemedi.');
        } finally {
            setLoading(false);
        }
    }, [year, week]);

    useEffect(() => { load(); }, [load]);

    // Hafta değişince açık döküm kapanır: önceki haftanın restoran listesi yeni haftanın
    // kartının altında bir an bile görünmemeli.
    useEffect(() => { setOpenModel(null); }, [year, week]);

    const open = groups.find(g => g.model === openModel);

    return (
        <section className="fmb" aria-label="Haftanın paketleri iş modeline göre">
            <div className="fmb-head">
                <h2>Bu haftanın paketleri</h2>
                <p>Her restoranla yapılan anlaşmaya göre paranın izlediği yol.</p>
            </div>

            {error && (
                <Notice tone="warn" title="Model dökümü gösterilemiyor" compact>
                    {error} Aşağıdaki hakediş kayıtları bundan etkilenmez.
                </Notice>
            )}

            <div className="fmb-cards">
                {[1, 2, 3].map(model => {
                    const meta = MODELS[model];
                    const group = groups.find(g => g.model === model) || { model, packageCount: 0, partners: [] };
                    const hasPackages = group.packageCount > 0;
                    const isOpen = openModel === model;

                    return (
                        <article key={model} className={`fmb-card fmb-${meta.tone} ${isOpen ? 'is-open' : ''}`}>
                            <div className="fmb-card-top">
                                <span className="fmb-model-no">Model {model}</span>
                                <h3>{meta.title}</h3>
                            </div>

                            {/* Paranın yolu: kartın asıl bilgisi. Son durak "kimde kalıyor"u söyler. */}
                            <ol className="fmb-path" aria-label={`Paranın yolu: ${meta.path.join(', ')}`}>
                                {meta.path.map((stop, i) => (
                                    <li key={stop} className={stop === 'Siz' ? 'is-you' : ''}>
                                        {i > 0 && <ChevronRight size={13} aria-hidden="true" />}
                                        <span>{stop}</span>
                                    </li>
                                ))}
                            </ol>

                            <div className="fmb-figures">
                                <div>
                                    <span className="fmb-label">Paket</span>
                                    <strong>{loading ? '…' : group.packageCount}</strong>
                                </div>
                                <div className="fmb-amount">
                                    <span className="fmb-label">{meta.amountLabel}</span>
                                    <strong>{loading ? '…' : hasPackages ? amountOf(group) : '—'}</strong>
                                </div>
                            </div>

                            <p className="fmb-note">{!loading && !hasPackages ? meta.empty : meta.note}</p>

                            {hasPackages && (
                                <button
                                    type="button"
                                    className="fmb-toggle"
                                    aria-expanded={isOpen}
                                    onClick={() => setOpenModel(isOpen ? null : model)}
                                >
                                    <ChevronDown size={14} className={isOpen ? 'is-flipped' : ''} />
                                    {isOpen ? 'Restoranları gizle' : `${group.partners.length} restoranı göster`}
                                </button>
                            )}
                        </article>
                    );
                })}
            </div>

            {open && (
                <div className="fmb-partners">
                    <div className="fmb-partners-head">
                        <strong>Model {open.model} — {MODELS[open.model].title}</strong>
                        <span>{open.packageCount} paket</span>
                    </div>
                    <div className="fmb-table-wrap">
                        <table className="fmb-table">
                            <thead>
                                <tr>
                                    <th>Restoran</th>
                                    <th className="num">Paket</th>
                                    {open.model === 1 && <th className="num">Paket bedeli</th>}
                                    {open.model === 1 && <th className="num">Alacağınız</th>}
                                    {open.partners.some(p => p.payableAmount > 0) && <th className="num">Paket başı bedel</th>}
                                    {open.partners.some(p => p.payableAmount > 0) && <th className="num">Ödeyeceğiniz</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {open.partners.map((p, i) => (
                                    <tr key={`${p.partnerId}-${i}`}>
                                        <td>{p.partnerName || 'Adı kayıtlı değil'}</td>
                                        <td className="num">{p.packageCount}</td>
                                        {open.model === 1 && <td className="num">{formatTL(p.grossPackageAmount)}</td>}
                                        {open.model === 1 && <td className="num strong">{formatTL(p.receivableAmount)}</td>}
                                        {open.partners.some(x => x.payableAmount > 0) && (
                                            <td className="num">{p.integrationFeePerPackage ? formatTL(p.integrationFeePerPackage) : '—'}</td>
                                        )}
                                        {open.partners.some(x => x.payableAmount > 0) && (
                                            <td className="num strong">{p.payableAmount > 0 ? `${formatTL(p.payableAmount)} + KDV` : '—'}</td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </section>
    );
};

export default FleetModelBreakdown;
